import { type FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useNewSupportCall from '../../../hooks/useNewSupportCall';
import type { FetchNewSupportCallProps, FormFields } from '../../../models/newSupportCall';
import moment from 'moment';
import Notification from '@rio-cloud/rio-uikit/Notification';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Button from '@rio-cloud/rio-uikit/Button';
import Select from '@rio-cloud/rio-uikit/Select';
import AdministrativeForm from './forms/administrative';
import TechnicalForm from './forms/technical';
import CancellationForm from './forms/cancellation';
import { fetchOccurrencesDetail, uploadFileSupport } from '../../../api/tas';
import SimpleUpload from "../components/SimpleUpload";
import { useSelector } from 'react-redux';
import { getUserProfile } from '../../../configuration/login/loginSlice';
import { fetchUser } from '../../../api/fetchUser';
import { F2_FORM_SUBJECTS, optionsAdministrative, optionsFinancial, optionsTechnical, optionsVolksTotalPortal, supportStatusOptions, supportSubjectOptions, SupportType } from '../constants';
import { getNewSupportCallValidationSummary, validateNewSupportCallForm } from './validateNewSupportCallForm';
import { isTasSupportSubject } from './tasAdditionalRequiredFields';
import TasForm from './forms/tas/TasForm';
import VehicleData from '../pages/details/components/VehiclesData';
import SearchAutocomplete from '../../../components/form/searchAutocomplete';
import { fetchOccurrenceSummary, IOccurrenceSummary } from '../../../api/fetchOccurence';
import { BudgetRecipientEnum } from '../../../components/budgetsForm/createAndEdit/types';
import { fetchBudgetUserProfile } from '../../../api/fetchBudget';
import MainOccurenceCard from '../components/MainOcurrenceCard';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useIntl } from 'react-intl';

const initialFormData: FormFields = {
    supportType: 'VOLKSTOTAL',
    subject: 'ADMINISTRATIVE',
    subtopic: 'MANUAL_CONTRACT_REGISTRATION',
    responsible: '',
    chassis: '',
    licensePlate: '',
    dn: '',
    createdBy: '',
    customer: '',
    hourMeter: '',
    odometer: '',
    entryDate: '',
    regionalId: '',
    dnResponsible: '',
    phone: '',
    occurrenceOriginId: '',
    details: '',
    partName: '',
    aeg: '',
    customerComment: '',
    diagnose: '',
    repair: '',
    analysisReason: '',
    contractNumber: '',
    vehicleQtd: '',
    cancelReason: '',
    cancelComment: '',
    assignedTo: '',
    status: '',
    createdAt: '',
    updatedAt: '',
};

const NewSupportCallPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const userProfile = useSelector(getUserProfile);
    const [loggedUserName, setLoggedUserName] = useState("");
    const [profile, setProfile] = useState<BudgetRecipientEnum | undefined>(undefined);
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    const [searchParams] = useSearchParams();
    const mainOccurrenceId = searchParams.get('mainOccurrenceId');

    const showVinculateOccurrence = useFeatureFlag('tasOccurrenceVinculateOccurrence');

    useEffect(() => {
        if(mainOccurrenceId) {
            setOccurrenceId(mainOccurrenceId)
        }
    }, [mainOccurrenceId])

    const [formData, setFormData] = useState<FormFields>(initialFormData);
    const [errors, setErrors] = useState<Partial<Record<keyof FormFields, string>>>({});

    const updateField = <K extends keyof FormFields>(field: K, value: FormFields[K]) => {
        setFormData(previousValue => ({
            ...previousValue,
            [field]: value,
        }));
    };

    const {
        role,
        vehicle,
        isLoadingVehicleFetch,
        duplicateOpenOccurrences,
        occurrenceId,
        mainOccurrenceData,
        fetchVehicle,
        checkDuplicateChassis,
        createSupportCall,
        setOccurrenceId,
        handleClearOccurrence,
    } = useNewSupportCall(formData, updateField);

    const getUserData = async () => {
        if (!userProfile || !userProfile.sub) return;

        const userId = userProfile.sub.split(':')[1];
        const response = await fetchUser(userId);
        if (!response) return;

        const fullName = (response.firstName ?? '') + ' ' + (response.lastName ?? '');
        setLoggedUserName(fullName);
        setFormData(previous => {
            if (previous.responsible === fullName) return previous;
            return { ...previous, responsible: fullName };
        });
    }

    useEffect(() => {
        const loadProfile = async () => {
            const profileFetched = await fetchBudgetUserProfile();
            if (profileFetched) {
                setProfile(profileFetched);
            }
        };
        loadProfile();
    }, []);

    useEffect(() => {
        getUserData();
    }, [userProfile]);

    const handleBackClick = () => {
        navigate(-1);
    };

    const validateForm = (): boolean => {
        const newErrors = validateNewSupportCallForm(formData, loggedUserName);
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            return true;
        }

        Notification.error(getNewSupportCallValidationSummary(newErrors));
        return false;
    };

    const updateSupportType = (value: string) => updateField('supportType', value);
    const updateSubject = (value: string) => {
        updateField('subject', value);
        updateField('subtopic', '');
    }
    const updateSubtopic = (value: string) => updateField('subtopic', value);
    const location = useLocation();

    const handleNavigateHome = () => {
        const params = new URLSearchParams(location.search)
        params.delete('id')
        params.delete('mainOccurrenceId')
        const basePath = location.pathname.substring(
            0,
            location.pathname.lastIndexOf('/')
        );

        navigate(`${basePath}?${params.toString()}`);
    }

    const handleCancelNewSupportCall = () => {
        setShowDuplicateChassisModal(false);
        handleNavigateHome();
    };
    const handleNewData = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm()) return;

        const groupedValues = { ...formData };

        const payload: FetchNewSupportCallProps = {
            supportType: groupedValues.supportType,
            responsible: loggedUserName || groupedValues.responsible,
            chassis: groupedValues.chassis,
            licensePlate: groupedValues.licensePlate,
            dn: groupedValues.dn,
            dnResponsible: groupedValues.dnResponsible || loggedUserName || groupedValues.responsible,
            customer: groupedValues.customer,
            hourMeter: Number(groupedValues.hourMeter) || undefined,
            odometer: Number(groupedValues.odometer) || undefined,
            entryDate: moment(groupedValues.entryDate).toISOString(),
            regionalId: Number(groupedValues.regionalId) || undefined,
            subject: groupedValues.subject,
            customerComment: groupedValues.customerComment,
            status: 'OPEN',
            phone: groupedValues?.phone,
            partName: groupedValues?.partName,
            diagnose: groupedValues?.diagnose,
            repair: groupedValues?.repair,
            analysisReason: groupedValues?.analysisReason,
            contractNumber: groupedValues.contractNumber,
            vehicleQtd: Number(groupedValues.vehicleQtd) || undefined,
            cancelReason: groupedValues.cancelReason,
            cancelComment: groupedValues.cancelComment,
            requestDetail: groupedValues.requestDetail,
            aeg: groupedValues.aeg,
            subtopic: groupedValues.subtopic || undefined,
            partOrComponentName: groupedValues.partOrComponentName,
            partNumber: groupedValues.partNumber,
            problemDescription: groupedValues.problemDescription,
            failureFrequency: groupedValues.failureFrequency,
            problemSteps: groupedValues.problemSteps,
            diagnosticAndResults: groupedValues.diagnosticAndResults,
            mainOccurrenceId: occurrenceId
        };

        setLoading(true)

        const response = await createSupportCall(payload);

        if (!response) {
            Notification.error(intl.formatMessage({ id: "intl-msg:tas.newSupportCall.create.error" }));
            setLoading(false);
            return;
        }

        const failedUploadFileNames: string[] = [];

        for (const file of files) {
            const uploaded = await uploadFileSupport({ path: response.id, file: [file] });
            if (!uploaded) {
                failedUploadFileNames.push(file.name);
            }
        }

        setFormData(initialFormData);
        setErrors({});
        setFiles([]);

        if (failedUploadFileNames.length) {
            Notification.error(intl.formatMessage(
                { id: "intl-msg:tas.newSupportCall.create.uploadErrorAfterCreate" },
                { fileNames: failedUploadFileNames.join(', ') },
            ));
        } else {
            Notification.success(intl.formatMessage({ id: "intl-msg:tas.newSupportCall.create.success" }));
        }

        if (id) {
            handleNavigateHome()
        } else {
            handleBackClick();
        }

        setLoading(false)
    };

    const subject = formData.subject;

    const getSubjectOptions = () => {
        if (formData.supportType === 'TECHNICAL_SUPPORT') {
            return supportSubjectOptions;
        }

        return supportStatusOptions;
    }

    const setSubjectValue = () => {
        if (subject === SupportType.ADMINISTRATIVE) {
            updateSubtopic(optionsAdministrative[0].id);
        }

        if (subject === SupportType.FINANCIAL) {
            updateSubtopic(optionsFinancial[0].id);
        }

        if (subject === SupportType.PORTAL) {
            updateSubtopic(optionsVolksTotalPortal[0].id);
        }

        if (subject === SupportType.TECHNICAL) {
            updateSubtopic(optionsTechnical[0].id);
        }

        if (
            F2_FORM_SUBJECTS.includes(subject as string) ||
            subject === 'CABIN' ||
            subject === 'EXHAUST_SYSTEM_AND_AFTER_TREATMENT' ||
            subject === 'SOFTWARE_DIESEL_VEHICLE' ||
            subject === 'ENGINE_REPLACEMENT_REQUEST' ||
            subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN' ||
            subject === 'TAS_DIAGNOSIS_TOOLS'
        ) {
            updateSubtopic(optionsTechnical[0].id);
        }
    }

    useEffect(() => {
        setSubjectValue();
        setErrors({});
    }, [subject])

    const [showDuplicateChassisModal, setShowDuplicateChassisModal] = useState(false);

    useEffect(() => {
        checkDuplicateChassis(formData.chassis || '');
    }, [formData.chassis]);

    useEffect(() => {
        if (duplicateOpenOccurrences.length > 0) {
            setShowDuplicateChassisModal(true);
        }
    }, [duplicateOpenOccurrences]);

    useEffect(() => {
        if (vehicle?.licensePlate) {
            setFormData(({
                ...formData,
                licensePlate: vehicle?.licensePlate,
            }));
        }
    }, [vehicle?.licensePlate])

    const getThemeSelectOptions = () => {
        if (subject === SupportType.ADMINISTRATIVE) {
            return optionsAdministrative;
        }

        if (subject === SupportType.FINANCIAL) {
            return optionsFinancial;
        }

        if (subject === SupportType.PORTAL) {
            return optionsVolksTotalPortal;
        }

        if (subject === SupportType.TECHNICAL) {
            return optionsTechnical;
        }

        return [];
    }

    const fetchOccurrencesDetails = async () => {
        setLoading(true);

        try {
            if (!id) {
                throw new Error('NO_DATA');
            }

            const res = await fetchOccurrencesDetail(id);
            if (res.regionalId) {
                updateField('regionalId', res.regionalId?.toString());
            }

            updateField('supportType', res.supportType);
            updateField('responsible', res.responsible);
            updateField('chassis', res.chassis);
            updateField('subtopic', res.subtopic);
            updateField('licensePlate', res.licensePlate);
            updateField('dn', res.dn);
            updateField('dnResponsible', res.dnResponsible);
            updateField('phone', res.phone);
            updateField('createdBy', res.createdBy);
            updateField('customer', res.customer);
            updateField('hourMeter', res.hourMeter);
            updateField('odometer', res.odometer);
            updateField('entryDate', res.entryDate ? moment(res.entryDate).format('YYYY-MM-DD') : '');
            updateField('occurrenceOriginId', res.occurrenceOriginId);
            updateField('details', res.details);
            updateField('subject', res.subject);
            updateField('partName', res.partName);
            updateField('aeg', res.aeg);
            updateField('customerComment', res.customerComment);
            updateField('diagnose', res.diagnose);
            updateField('repair', res.repair);
            updateField('analysisReason', res.analysisReason);
            updateField('contractNumber', res.contractNumber);
            updateField('vehicleQtd', res.vehicleQtd);
            updateField('cancelReason', res.cancelReason);
            updateField('cancelComment', res.cancelComment);
            updateField('assignedTo', res.assignedTo);
            updateField('status', res.status);
            updateField('createdAt', res.createdAt);
            updateField('updatedAt', res.updatedAt);
            updateField('requestDetail', res.requestDetail);
            updateField('partOrComponentName', res.partOrComponentName);
            updateField('partNumber', res.partNumber);
            updateField('problemDescription', res.problemDescription);
            updateField('failureFrequency', res.failureFrequency);
            updateField('problemSteps', res.problemSteps);
            updateField('diagnosticAndResults', res.diagnosticAndResults);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formData.supportType === 'TECHNICAL_SUPPORT') {
            updateSubject(supportSubjectOptions[0].id);
            return;
        }

        updateSubject(supportStatusOptions[0].id);
    }, [formData.supportType])

    useEffect(() => {
        if (id) {
            fetchOccurrencesDetails();
        };
    }, [id]);

    return (
        <div className='panel panel-default panel-body height-100pct shadow-default margin-top-20' style={{ maxWidth: 1400, margin: '0 auto' }}>
            <header className='display-flex justify-content-between align-items-center border border-bottom-only padding-bottom-25'>
                <h1 className='text-capitalize text-color-darkest text-size-h4'>Nova solicitação</h1>
                <button onClick={handleBackClick} type='button' className='btn btn-muted'>
                    <span className='rioglyph rioglyph-remove margin-0 text-color-darkest' />
                </button>
            </header>
            <main className='padding-top-25 display-flex gap-40'>
                <form onSubmit={handleNewData} className='flex-1-1'>
                    <p className='text-size-16 text-bold'>1. Dados Iniciais</p>
                    <div className='padding-top-20'>
                        <p className='text-size-14 text-color-darker margin-bottom-5'>Tipo de suporte</p>
                        <div className='form-group display-flex'>
                            {role === 'FACTORY' || role === 'TOWER' ? (
                                <div>
                                    <label className='radio' htmlFor='radio1'>
                                        <input
                                            type='radio'
                                            id='radio1'
                                            name='radioGroup'
                                            value='TECHNICAL_SUPPORT'
                                            checked={formData.supportType === 'TECHNICAL_SUPPORT'}
                                            onChange={event => updateSupportType(event.target.value)}
                                        />
                                        <span className='radio-text'>
                                            <span>TAS - Suporte Técnico</span>
                                        </span>
                                    </label>
                                </div>
                            ) : null}
                            <div>
                                <label className='radio' htmlFor='radio2'>
                                    <input
                                        type='radio'
                                        id='radio2'
                                        name='radioGroup'
                                        value='VOLKSTOTAL'
                                        checked={formData.supportType === 'VOLKSTOTAL'}
                                        onChange={event => updateSupportType(event.target.value)}
                                    />
                                    <span className='radio-text'>
                                        <span>Suporte Volkstotal</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className='padding-top-20'>
                        <span className='text-size-14 text-color-darker'>Assunto</span>
                        <div className='form-group form-group-lg padding-top-10'>
                            <Select
                                bsSize='lg'
                                value={formData.subject ? [formData.subject] : []}
                                onChange={option => updateSubject(option?.id || '')}
                                options={getSubjectOptions()}
                                placeholder="Selecione o assunto"
                            />
                        </div>
                    </div>

                    {subject !== SupportType.COMMERCIAL && subject !== SupportType.CONTRACT_CANCELLATION && formData.supportType !== "TECHNICAL_SUPPORT" ? (
                        <div className=''>
                            <span className='text-size-14 text-color-darker'>Tema</span>
                            <div className='form-group form-group-lg padding-top-10'>
                                <Select
                                    bsSize='lg'
                                    value={formData.subtopic ? [formData.subtopic] : []}
                                    onChange={option => updateSubtopic(option?.id || '')}
                                    options={getThemeSelectOptions()}
                                    placeholder="Selecione o tema"
                                />
                            </div>
                        </div>
                    ) : null}

                    {showVinculateOccurrence ? (
                        <>
                            {!mainOccurrenceData && !occurrenceId ? (<div className='padding-top-20'>
                                <label
                                    htmlFor="occurrenceSearch"
                                    className="text-size-14 text-color-darker"
                                >
                                    {intl.formatMessage({ id: "intl-msg:tas.occurrence.btn-search" })}
                                </label>
                                <div className="form-group form-group-lg padding-top-10">
                                    <SearchAutocomplete
                                        id="occurrenceSearch"
                                        key={occurrenceId ?? 'no-occurrence'}
                                        inputClassName="form-control-lg"
                                        placeholder={intl.formatMessage({ id: "intl-msg:tas.occurrence.enter-occurrence-number" })}
                                        fetchOptions={(searchTerm: string) =>
                                            fetchOccurrenceSummary(
                                                profile,
                                                searchTerm,
                                                undefined,
                                                formData.chassis
                                            )
                                        }
                                        optionLabel={{
                                            protocolNumber: intl.formatMessage({ id: "intl-msg:tas.occurrence.occurrence" }),
                                            vehicleChassis: intl.formatMessage({ id: "intl-msg:tas.occurrence.chassis" }),
                                        }}
                                        onSelectedOption={(occurrence: IOccurrenceSummary) => {
                                            setOccurrenceId(occurrence.uuid);
                                        }}
                                        valueRenderer={(option: IOccurrenceSummary) =>
                                            `${option.protocolNumber} - ${option.vehicleChassis}`
                                        }
                                        optionRenderer={(option: IOccurrenceSummary) => (
                                            <div className="truncate">
                                                <div className="font-medium">{option.protocolNumber}</div>
                                                <div className="text-sm text-gray-500">
                                                    {intl.formatMessage({ id: "intl-msg:tas.occurrence.chassis" })}: {option.vehicleChassis}
                                                </div>
                                            </div>
                                        )}
                                        onClear={() => {
                                            handleClearOccurrence();
                                        }}
                                    />
                                </div>
                            </div>) : null}

                            {mainOccurrenceData && occurrenceId ? (
                                <MainOccurenceCard
                                    mainOccurrenceData={mainOccurrenceData}
                                    onClear={handleClearOccurrence}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {(
                        subject === 'ADMINISTRATIVE' ||
                        subject === 'FINANCIAL' ||
                        subject === 'PORTAL' ||
                        subject === 'COMMERCIAL'
                    ) ? (
                        <AdministrativeForm
                            formData={formData}
                            errors={errors}
                            updateField={updateField}
                            subject={subject}
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                            occurrenceId={occurrenceId}
                        />
                    ) : null}

                    {subject === 'TECHNICAL' ? (
                        <TechnicalForm
                            formData={formData}
                            errors={errors}
                            updateField={updateField}
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                            occurrenceId={occurrenceId}
                        />
                    ) : null}

                    {subject === 'CONTRACT_CANCELLATION' ? (
                        <CancellationForm
                            formData={formData}
                            errors={errors}
                            updateField={updateField}
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                            occurrenceId={occurrenceId}
                        />
                    ) : null}

                    {isTasSupportSubject(subject as string) ? (
                        <TasForm
                            formData={formData}
                            errors={errors}
                            updateField={updateField}
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                            occurrenceId={occurrenceId}
                        />
                    ) : null}

                    <div className="margin-top-20">
                        <SimpleUpload
                            addFileToUpload={(pickedFiles) => setFiles((prev) => [...prev, ...pickedFiles])}
                            removeFileToUpload={(fileName) => setFiles((prev) => prev.filter((f) => f.name !== fileName))}
                        />
                    </div>

                    <div className='form-group margin-top-20'>
                        <div className='display-flex justify-content-end'>
                            {!loading && (
                                <>
                                    <button onClick={handleBackClick} type='reset' className='btn btn-default margin-right-10'>
                                        Cancelar
                                    </button>
                                    <button type='submit' className='btn btn-primary'>
                                        Criar suporte
                                    </button>
                                </>
                            )}
                            {loading && <Spinner />}
                        </div>
                    </div>
                </form>
                <div className="min-width-400 max-width-400 height-100pct">
                    <VehicleData data={vehicle} isLoading={isLoadingVehicleFetch} />
                </div>
            </main>
            <Dialog
                show={showDuplicateChassisModal}
                bsSize="sm"
                title={intl.formatMessage({ id: 'intl-msg:tas.support.duplicateChassis.title' })}
                body={
                    <div className='text-size-14 margin-0'>
                        <p className='margin-0'>
                            {intl.formatMessage(
                                { id: 'intl-msg:tas.support.duplicateChassis.openOccurrences' },
                                { count: duplicateOpenOccurrences.length },
                            )}
                        </p>
                        {duplicateOpenOccurrences.length ? (
                            <ul className='margin-top-5 margin-bottom-5 text-ellipsis list-disc'>
                                {duplicateOpenOccurrences.map(occurrence => {
                                    return (
                                        <li key={occurrence.id}>
                                            <span className='text-bold'>
                                                {occurrence?.tasOccurrenceNumber || occurrence?.id}
                                            </span>
                                        </li>
                                    )
                                })}
                            </ul>
                        ) : null}
                        <p className='margin-0'>
                            {intl.formatMessage({ id: 'intl-msg:tas.support.duplicateChassis.canContinue' })}
                        </p>
                        <strong className='display-block margin-top-10'>
                            {intl.formatMessage({ id: 'intl-msg:tas.support.duplicateChassis.confirmation' })}
                        </strong>
                    </div>
                }
                footer={
                    <div className='display-flex justify-content-end gap-10'>
                        <Button onClick={() => setShowDuplicateChassisModal(false)} className='btn btn-primary' bsSize='lg'>
                            {intl.formatMessage({ id: 'intl-msg:tas.support.duplicateChassis.yes' })}
                        </Button>
                        <Button onClick={handleCancelNewSupportCall} className='btn btn-default' bsSize='lg'>
                            {intl.formatMessage({ id: 'intl-msg:tas.support.duplicateChassis.no' })}
                        </Button>
                    </div>
                }
                onCloseValidation={() => {
                    setShowDuplicateChassisModal(false);
                    return true;
                }}
                showCloseButton
            />
        </div>
    );
};

export default NewSupportCallPage;
